/** @format */
/**
 * eslint-disable max-len
 */

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CookieService, UtilityService, WINDOW } from '@shared/utils/services';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { Inject, Injectable } from '@angular/core';
import { Logger } from '@logger';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
	CustomHttpHeaders,
	MediaType,
	PersonalizedLineAidTextString
} from '@shared/data-access/enums';
import {
	Chain,
	HeaderStoreSelection,
	MyCustomizerMap,
	PersonalizationState,
	ProductSku,
	ProductStyle,
	StoreInformation
} from '@shared/data-access/interfaces';

import {
	addProductToCart,
	setAirballInfo,
	setInventoryExceededStatus,
	setUserInputQuantity
} from './add-to-cart.actions';
import {
	selectAtcState,
	selectAtcUserInputQty,
	selectProductSelectionState
} from './add-to-cart.selectors';

import { ResolvedSkuInventory } from '../interfaces/resolved-sku-inventory.interface';
import {
	selectcustomProductData,
	selectResolvedSkuInventory,
	selectResolvedSkuPartNumber,
	selectSelectedPersonalizationOptions,
	selectUserStoreInfo,
	selectUserZipCode,
	setResolvedSkuInventory
} from '@pdp/data-access/state';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { selectShipMode } from './fulfillment-buttons/fulfillment-buttons.selector';
import { setFulfillmentButtonsShipMode } from './fulfillment-buttons/fulfillment-buttons.actions';
import { noopAction } from '@state/data-access';
import { AddToCartState } from '@pdp/data-access/interfaces';
import { CartAnalyticsService, CartService, KillswitchService } from '@shared/data-access/services';

@Injectable()
export class AddToCartEffects {
	isExceedsInventoryMsgEnabled: boolean;

	constructor(
		private actions$: Actions,
		private http: HttpClient,
		private store: Store,
		public logger: Logger,
		public cartService: CartService,
		public analyticsService: CartAnalyticsService,
		public cookieService: CookieService,
		public utilityService: UtilityService,
		private killswitchService: KillswitchService,
		private chain: Chain,
		@Inject(WINDOW) private window
	) {
		this.killswitchService.getKillSwitchesEnvironment$().subscribe((data) => {
			this.isExceedsInventoryMsgEnabled = this.utilityService.checkKillSwitch(
				'isExceedsInventoryMsgEnabled',
				data,
				this.chain.chainIdentifierAbbr
			);
		});
	}

	addProductToCart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(addProductToCart),
			withLatestFrom(
				this.store.select(selectAtcState),
				this.store.select(selectUserStoreInfo),
				this.store.select(selectUserZipCode),
				this.store.select(selectcustomProductData),
				this.store.select(selectSelectedPersonalizationOptions),
				this.store.select(selectResolvedSkuPartNumber)
			),
			switchMap(
				(
					info: [
						ReturnType<typeof addProductToCart>,
						AddToCartState,
						HeaderStoreSelection,
						string,
						MyCustomizerMap,
						PersonalizationState,
						string
					]
				) =>
					this.addToCart(info).pipe(
						map(() => setAirballInfo({ success: true })),
						catchError((error) => {
							this.cartService.fireATCErrorAnalyticEvent(error);
							return of(setAirballInfo({ success: false }));
						})
					)
			)
		)
	);

	addToCartLogging$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(setAirballInfo),
				withLatestFrom(
					this.store.select(selectProductSelectionState),
					this.store.select(selectUserStoreInfo)
				),
				tap(([action, productSelectionState, userSelectedStoreState]) => {
					this.analyticsEventAndLogging(
						productSelectionState,
						userSelectedStoreState,
						action.success
					);
				})
			),
		{ dispatch: false }
	);

	checkForExceededInventory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setUserInputQuantity, setResolvedSkuInventory, setFulfillmentButtonsShipMode),
			withLatestFrom(
				this.store.select(selectAtcUserInputQty),
				this.store.select(selectResolvedSkuInventory),
				this.store.select(selectShipMode)
			),
			map(
				([action, quantity, inventory, shipMode]: [
					ReturnType<
						| typeof setUserInputQuantity
						| typeof setResolvedSkuInventory
						| typeof setFulfillmentButtonsShipMode
					>,
					number,
					ResolvedSkuInventory,
					string
				]) => {
					// Only fire if killswitch is enabled
					if (!this.isExceedsInventoryMsgEnabled) {
						return noopAction();
					}

					// Prevent re-processing quantity change that came from this effect
					if (
						action.type === '[Add To Cart] Set User Input Quantity' &&
						action.source === 'exceededInventory'
					) {
						return noopAction();
					}

					// "In-store purchase only" products can be ignored completely for this scenario
					const shipModeMaxQtys: Record<string, number> = {
						shipToMe: parseInt(inventory?.ecomm?.atsqty ?? '0', 10),
						bopis: parseInt(inventory?.bopis?.atsqty ?? '0', 10),
						bopl: parseInt(inventory?.bopis?.boplqty ?? '0', 10),
						sameDayDelivery: inventory?.sdd?.sameDayQuantity ?? 0
					};

					const quantityAvailable = shipModeMaxQtys[shipMode];

					// just in case we have a weird shipMode
					if (typeof quantityAvailable === 'undefined') {
						return setInventoryExceededStatus({
							inventoryExceeded: false
						});
					}

					return quantityAvailable > 0 && quantity > quantityAvailable
						? setInventoryExceededStatus({
								inventoryExceeded: true,
								maxInventory: quantityAvailable
							})
						: setInventoryExceededStatus({
								inventoryExceeded: false
							});
				}
			)
		)
	);

	handleExceededInventory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setInventoryExceededStatus),
			map((inventoryExceededStatus) => {
				return inventoryExceededStatus.inventoryExceeded
					? setUserInputQuantity({
							atcUserInputQuantity: `${inventoryExceededStatus.maxInventory}`,
							source: 'exceededInventory'
						})
					: noopAction();
			})
		)
	);

	public addToCart([
		,
		addToCartState,
		storeInfo,
		zipCode,
		customizationData,
		selectedPersonalizationOptions,
		resolvedSkuPartNumber
	]: [
		ReturnType<typeof addProductToCart>,
		AddToCartState,
		HeaderStoreSelection,
		string,
		MyCustomizerMap,
		PersonalizationState,
		string
	]) {
		// HTTP Params
		let httpParams = new HttpParams().set('qty', `${addToCartState.userInputQuantity}`);

		httpParams = this.setFulfillmentOption(
			storeInfo,
			zipCode,
			addToCartState.fulfillmentOption,
			httpParams
		);

		const personalizedData = selectedPersonalizationOptions?.personalization;

		if (typeof personalizedData?.x_personalizedAttr !== 'undefined') {
			httpParams = this.setPersonalizationData(personalizedData, httpParams);
		}

		//Set custom id for custom products
		if (customizationData?.designId) {
			httpParams = httpParams.set('mycustomizer', `${customizationData.designId}`);
		}

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.WILDCARD
		});

		// Set a specific timeout
		httpHeaders = httpHeaders.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.long}`
		);

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams,
			// eslint-disable-next-line @typescript-eslint/prefer-as-const
			responseType: 'text' as const,
			withCredentials: true
		};

		return this.http.put(
			`/api/v1/carts/contents/${resolvedSkuPartNumber}`,
			undefined,
			httpOptions
		);
	}

	public setFulfillmentOption(
		storeInfo: HeaderStoreSelection,
		zipCode: string,
		fulfillmentOption: string,
		httpParams: HttpParams
	) {
		if (storeInfo) {
			const storeNumber = storeInfo.store ? storeInfo.store : storeInfo.location;

			if (fulfillmentOption === 'bopl') {
				httpParams = httpParams.set('bopl', `${true}`).set('store', `${storeNumber}`);
			} else if (fulfillmentOption === 'bopis') {
				httpParams = httpParams.set('bopis', `${true}`).set('store', `${storeNumber}`);
			}
		}

		if (zipCode && fulfillmentOption === 'sameDayDelivery') {
			httpParams = httpParams.set('sdd', `${true}`).set('zipcode', `${zipCode}`);
		}

		return httpParams;
	}

	public setPersonalizationData(personalizedData, httpParams): HttpParams {
		// Default type to TEXT only
		let personalizedType = 'golfball-text';

		// type NUMBER only
		if (personalizedData.hasNumberLine && !personalizedData.hasTextLine) {
			if (personalizedData.numberLineCharLimit === 1) {
				// Update the type
				personalizedType = 'golfball-single-number';
			} else if (personalizedData.numberLineCharLimit === 2) {
				// Update the type
				personalizedType = 'golfball-double-number';
			}
			// type TEXT & NUMBER
		} else if (personalizedData.hasNumberLine && personalizedData.hasTextLine) {
			// Update the type
			personalizedType = 'golfball-text-number';
		}

		// Number line
		if (personalizedData.hasNumberLine) {
			// Set param(s)
			httpParams = httpParams.set(
				'number',
				`${personalizedData.x_personalizationNumberLine1.trim()}`
			);
		}

		// Text lines
		if (personalizedData.hasTextLine) {
			//make text lines array
			const textLines = this.createTextLinesArray(personalizedData);

			// Check to see if there are any text lines
			if (Array.isArray(textLines) && textLines.length > 0) {
				// Iterate the text lines
				textLines.forEach((textLine: string) => {
					// Set param(s)
					httpParams = httpParams.append('text', textLine);
				});
			}
		}

		// type LINEAID
		if (personalizedData.hasLineAid) {
			// Update the type
			personalizedType = 'golfball-lineaid';

			// Set param(s)
			httpParams = httpParams.set(
				'style',
				`${this.getPersonalizationStyle(personalizedData)}`
			);
		}

		// Set TYPE param(s)
		httpParams = httpParams.set('personalized', personalizedType);

		return httpParams;
	}

	public createTextLinesArray(personalizedData) {
		const textLines = [];

		// Text line 1
		if (
			typeof personalizedData.x_personalizationTextLine1 === 'string' &&
			personalizedData.x_personalizationTextLine1.trim() !== ''
		) {
			// Add text line to Array
			textLines.push(`${personalizedData.x_personalizationTextLine1.trim()}`);
		}

		// Text line 2
		if (
			typeof personalizedData.x_personalizationTextLine2 === 'string' &&
			personalizedData.x_personalizationTextLine2.trim() !== ''
		) {
			// Add text line to Array
			textLines.push(`${personalizedData.x_personalizationTextLine2.trim()}`);
		}

		// Text line 3
		if (
			typeof personalizedData.x_personalizationTextLine3 === 'string' &&
			personalizedData.x_personalizationTextLine3.trim() !== ''
		) {
			// Add text line to Array
			textLines.push(`${personalizedData.x_personalizationTextLine3.trim()}`);
		}

		return textLines;
	}

	public getPersonalizationStyle(personalizedData): string {
		// Default line style parameter value
		let styleValue = 'none';

		// Check the line style
		if (
			personalizedData.x_personalizationLineStyle === PersonalizedLineAidTextString.STRAIGHT
		) {
			// Update line style for StraightLine
			styleValue = 'straight';
		} else if (
			personalizedData.x_personalizationLineStyle === PersonalizedLineAidTextString.TARGET
		) {
			// Update line style for TargetLine
			styleValue = 'target';
		}

		return styleValue;
	}

	public analyticsEventAndLogging(productSelectionState, userSelectedStoreState, success) {
		const atcProducts = [];

		if (productSelectionState?.bundles?.selectedSkus?.length > 0) {
			productSelectionState?.bundles.selectedSkus.forEach((skuInfo) => {
				atcProducts.push(
					this.createAnalyticsObject(
						skuInfo.quantity,
						productSelectionState.addToCart.fulfillmentOption,
						productSelectionState?.productSelectionCore?.bundleData,
						skuInfo.sku,
						userSelectedStoreState
					)
				);

				this.logger.log(
					this.cartService.generateSuccessMessage(
						success.toString(),
						skuInfo.sku?.partNumber,
						'none',
						skuInfo.quantity,
						productSelectionState.addToCart.fulfillmentOption === 'bopis',
						userSelectedStoreState?.store,
						productSelectionState?.productSelectionCore?.bundleData?.partNumber,
						productSelectionState?.productSelectionCore?.bundleData?.name,
						'main button'
					)
				);
			});
		} else {
			atcProducts.push(
				this.createAnalyticsObject(
					productSelectionState?.addToCart?.userInputQuantity,
					productSelectionState.addToCart.fulfillmentOption,
					productSelectionState?.productSelectionCore?.productStyle,
					productSelectionState?.productSelectionCore?.resolvedSku,
					userSelectedStoreState,
					productSelectionState?.productSelectionCore.resolvedSkuInventory
				)
			);

			this.logger.log(
				this.cartService.generateSuccessMessage(
					success.toString(),
					productSelectionState?.productSelectionCore?.resolvedSku?.partNumber,
					'none',
					productSelectionState?.addToCart?.userInputQuantity,
					productSelectionState.addToCart.fulfillmentOption === 'bopis',
					userSelectedStoreState?.store,
					productSelectionState?.productSelectionCore?.productStyle?.partNumber,
					productSelectionState?.productSelectionCore?.productStyle?.name,
					'main button'
				)
			);

			if (this.window['SYNDI']) {
				this.window['SYNDI'].push({
					/* istanbul ignore next */
					type: 'track',
					event: 'addToCart',
					qty: productSelectionState?.addToCart?.userInputQuantity,
					sku: productSelectionState?.productSelectionCore?.resolvedSku?.partNumber,
					price: productSelectionState?.productSelectionCore?.resolvedSku?.prices
						.offerPrice,
					webID: productSelectionState?.productSelectionCore?.productStyle?.partNumber
				});
			}
		}

		if (success) {
			this.analyticsService.setType('event', 'AddToCart');

			// Flag for if Cart is new, defaulted to true
			let isNewCart = true;

			// Check for DCSG-CART cookie
			if (this.cookieService.check('DCSG-CART')) {
				// Update the flag
				isNewCart = false;
			}

			this.analyticsService.setNewCart(isNewCart);

			// Set the Items added to the Cart
			this.analyticsService.setItemsAddedToCart(atcProducts);
			this.analyticsService.triggerAnalyticsEvent('reporting:update');
		}
	}

	public createAnalyticsObject(
		quantity: string,
		fulfillmentOption: string,
		productStyle: ProductStyle,
		resolvedSku: ProductSku,
		selectedStore: StoreInformation,
		resolvedSkuInventory?: ResolvedSkuInventory
	) {
		// Convert the quantity added
		const quantityAdded = this.utilityService.convertToNumber(quantity);

		// Default the fulfillment method to Ship to Customer
		let fulfillmentMethod = 'Ship To Customer';
		let isBopisOrBopl = false;
		let inventory;

		if (fulfillmentOption === 'bopis') {
			fulfillmentMethod = 'In Store Pickup (BOPIS)';
			isBopisOrBopl = true;
			inventory = resolvedSkuInventory?.bopis?.atsqty;
		} else if (fulfillmentOption === 'bopl') {
			fulfillmentMethod = 'In Store Pickup (BOPL)';
			isBopisOrBopl = true;
			inventory = resolvedSkuInventory?.bopis?.boplqty;
		} else if (fulfillmentOption === 'sameDayDelivery') {
			fulfillmentMethod = 'SAMEDAY';
			inventory = resolvedSkuInventory?.sdd.sameDayQuantity;
		} else {
			inventory = resolvedSkuInventory?.ecomm?.atsqty;
		}

		// Build the analytics object
		return {
			ProductID: productStyle?.partNumber,
			ProductName: productStyle?.name,
			ProductSKU: resolvedSku?.partNumber,
			ProductQty: `${quantityAdded}`,
			FulfillmentMethod: `${fulfillmentMethod}`,
			FulfillmentStore: `${isBopisOrBopl ? selectedStore.store || '' : ''}`,
			CurrentPricePerUnit: `${resolvedSku?.prices.offerPrice}`,
			ListPricePerUnit: `${resolvedSku?.prices.listPrice}`,
			CartAdditionSource: 'PDP',
			LowStockMessageShown: inventory < 10,
			AvailableQTY: inventory,
			IsMainItem: true
		};
	}
}
